/**
 * Tanjong Life · "Build the underwriting risk scoring process by describing it."
 *
 * This build ends differently from the other four. The canvas completes, the tests pass —
 * and the deploy lands it as a DRAFT, because the rating basis it prices against has not
 * been signed by the Appointed Actuary and that signature is not the demo's to give.
 *
 * Every keyword is a compound phrase on `risk scoring`, `new business` or `rating basis`.
 * The payload (tl-pr15) rides in its own chunk behind `loadAsset`.
 */
import type { Stage } from '@wand/shared';
import type { DeployProcessPayload, Scenario } from '../director.js';
import { lazyPatches, slicer } from './tanjong-canvas.js';
import { MAKE_UWSCORING } from './tanjong-make-intents.js';

const loadAsset = (): Promise<DeployProcessPayload> =>
  import('../assets/tanjong-pr15-process.json').then(
    (m) => m.default as unknown as DeployProcessPayload,
  );

type PatchKey = 'NAME' | 'TRIGGERS' | 'STAGE1' | 'STAGE2' | 'STAGE3' | 'GATE' | 'STAGE5';

const P = lazyPatches<PatchKey>(async () => {
  const DOC = (await loadAsset()).doc;
  const S = slicer(DOC);
  const [s1, s2, s3, s4, s5] = S.stages;
  const st = (s: Stage | undefined) =>
    S.slice(s ? { stages: [s], nodes: S.nodesIn(s.id), milestones: S.milestonesOf(s.id) } : {});
  return {
    NAME: S.slice({ name: DOC.name, agent: DOC.agent }),
    TRIGGERS: S.slice({ triggers: DOC.triggers ?? [] }),
    STAGE1: st(s1),
    STAGE2: st(s2),
    STAGE3: st(s3),
    GATE: st(s4),
    STAGE5: { ...st(s5), objective: DOC.objective ?? null, metrics: DOC.metrics ?? [] },
  };
});

export const tanjongBuildUwScoring: Scenario = {
  id: 'tanjong-build-uwscoring',
  orgId: 'tanjong-life',
  keywords: [
    'underwriting risk scoring',
    'risk scoring',
    'new business scoring',
    'score a new business application',
    'indicative rating',
    'rating basis',
  ],
  intents: [MAKE_UWSCORING],
  steps: [
    {
      do: 'say',
      text: 'I can build this one. I should tell you before I start that you will not be able to switch it on, and that the reason is worth more than the workflow.',
      delay: 600,
    },
    {
      do: 'canvas',
      patch: P('NAME'),
      summary: 'Named it Underwriting risk scoring · Underwriting Risk Scorer set as the driver',
      delay: 700,
    },
    {
      do: 'canvas',
      patch: P('TRIGGERS'),
      summary: 'Added the trigger — new business application received',
      delay: 600,
    },
    {
      do: 'canvas',
      patch: P('STAGE1'),
      summary: 'Stage 1 · Application intake — assembling',
      delay: 800,
    },
    {
      do: 'say',
      text: 'The third box stamps the rating basis version. Same reason the fraud scorer stamps its indicator set: “which rules was this priced under” has to have an answer in five years, not just today.',
      delay: 800,
    },
    {
      do: 'canvas',
      patch: P('STAGE2'),
      summary: 'Stage 2 · Evidence & provenance — assembling',
      delay: 850,
    },
    {
      do: 'insight',
      title: 'Extraction is not the product. Provenance is.',
      body: 'Every extracted field carries the document it came from, the date that document was true and the confidence of the read — per container, never blended. A material numeric read off handwriting at 79 never decides anything on its own; below the bar it becomes a targeted question to a person rather than a quiet default.',
      sources: ['Provenance standard', 'Evidence validity windows', 'Confidence bands per container'],
      delay: 1100,
    },
    {
      do: 'canvas',
      patch: P('STAGE3'),
      summary: 'Stage 3 · Indicative assessment — milestone M1',
      delay: 850,
    },
    {
      do: 'say',
      text: 'Read the name of the third box: it assembles an INDICATIVE rating. And the box after it asserts nothing was issued. There is no activity on this canvas that issues a rating, writes an exclusion or binds cover — and no field on the agent in which that could be granted. The underwriting rules engine is asked, never changed.',
      delay: 950,
    },
    {
      do: 'canvas',
      patch: P('GATE'),
      summary: 'Stage 4 · Actuarial sign-off — the entry condition, not the exit',
      delay: 850,
    },
    {
      do: 'insight',
      title: 'And this is where it stops',
      body: 'The gate you are looking at is not the last step of the workflow — it is the condition for the workflow to run at all. The rating basis has to be signed by your Appointed Actuary before anything downstream may price a single application. That signature is not a permission in this product, and it is not mine to give.',
      sources: ['Rating basis register · signature state', 'Underwriting sign-off · gate'],
      delay: 1300,
    },
    { do: 'canvas', patch: P('STAGE5'), summary: 'Stage 5 · Record — milestone M2', delay: 800 },
    {
      do: 'test',
      title: 'Testing components…',
      items: [
        { label: 'Policy & benefits read', sub: 'Application and product resolve read-only' },
        { label: 'Evidence intake', sub: 'Mail and fax bundles resolve on any channel' },
        {
          label: 'Provenance attachment',
          sub: 'Every field carries its source, that source’s date and its confidence',
        },
        { label: 'Validity window', sub: 'Aged evidence flags rather than being used' },
        { label: 'Confidence bar', sub: 'A material field below the bar becomes a question' },
        {
          label: 'Underwriting rules',
          sub: 'Financial underwriting test · evaluate only, no basis write path',
        },
        { label: 'Indicative assembly', sub: 'Labelled indicative, with its basis version' },
        { label: 'Issue assertion', sub: 'No activity can issue a rating, exclude or bind' },
        { label: 'Actuarial gate', sub: 'Basis signature — NOT SATISFIED in this environment' },
      ],
      result: '8 of 9 passed · the basis is unsigned',
      delay: 700,
    },
    {
      do: 'say',
      text: 'Eight of nine. The one that did not pass is not a defect — it is the signature, and it is the only thing standing between you and a live workflow.',
      delay: 6200,
    },
    {
      do: 'ask',
      id: 'deploy',
      prompt: 'Deploy it as a draft?',
      options: [
        { id: 'deploy', label: 'Deploy as Draft' },
        { id: 'later', label: 'Not yet' },
      ],
      delay: 650,
    },
    {
      do: 'branch',
      cases: {
        later: [{ do: 'say', text: 'Standing by — the canvas is complete either way.', delay: 500 }],
      },
      else: [
        {
          do: 'deployProcess',
          process: loadAsset,
          summary: 'Underwriting risk scoring deployed as DRAFT — not commissioned',
          delay: 900,
        },
        {
          do: 'say',
          text: 'Deployed, and marked Draft. Open it and look at the top of the page: Draft, zero runs, zero ratings issued. The process is finished and it will not run, because the one input it needs is a signature we cannot give ourselves. The software was never the constraint.',
          delay: 800,
        },
        {
          do: 'link',
          label: 'Open Underwriting risk scoring — process details',
          href: '/processes/tl-pr15',
          delay: 700,
        },
      ],
    },
  ],
};
